import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../../../services/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../../theme';
import { useTranslation } from '../../../../hooks/useTranslation';
import { formatCpfCnpj, formatPhone } from '../../../../utils';
import Step1ContractData from './Step1ContractData';
import Step2Creditor from './Step2Creditor';
import Step3Guarantees from './Step3Guarantees';
import Step4Confirmation from './Step4Confirmation';

const StepperBar = ({ activeStep, colors, t }) => {
  const STEPS = [
    { label: t('createContractModal.steps.contractData') },
    { label: t('createContractModal.steps.creditor') },
    { label: t('createContractModal.steps.guarantees'), sublabel: t('createContractModal.steps.optional') },
    { label: t('createContractModal.steps.confirmation') },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 0,
        py: 3,
        px: 2,
      }}
    >
      {STEPS.map((step, index) => {
        const isCompleted = index < activeStep;
        const isCurrent = index === activeStep;

        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isCompleted || isCurrent ? colors.greenAccent[500] : '#e0e0e0',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '14px',
                  flexShrink: 0,
                }}
              >
                {isCompleted ? (
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                ) : (
                  index + 1
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: isCurrent ? 700 : 400,
                  color: isCurrent ? colors.greenAccent[500] : '#9e9e9e',
                  mt: 0.75,
                  textAlign: 'center',
                  lineHeight: 1.3,
                  maxWidth: 80,
                }}
              >
                {step.label}
                {step.sublabel && (
                  <Box component="span" sx={{ display: 'block', fontStyle: 'italic' }}>
                    {step.sublabel}
                  </Box>
                )}
              </Typography>
            </Box>

            {index < STEPS.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  backgroundColor: index < activeStep ? colors.greenAccent[500] : '#e0e0e0',
                  mt: '17px',
                  mx: 0.5,
                  minWidth: 40,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const initialFormData = {
  // Step 1
  dataEmissao: '',
  bancarizadorStep1: '',
  baseCalculo: 'DIAS_UTEIS_365',
  tabelaCalculo: 'PRICE',
  indice: 'CDI',
  carenciaPrincipal: 0,
  hasSimulated: false,

  // Step 2
  credor: '',
  bancarizador: '',
  ccbVia: '',
  ccbViaNaoNegociavel: '',
  termoCessao: '',

  // Step 3
  garantias: [{ id: Date.now(), value: '' }],
};

const CreateContractModal = ({ open, onClose, simulation }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    if (open) {
      api.get('/v1/contracts/options')
        .then(({ data }) => setOptions(data.data))
        .catch((err) => console.error('Erro ao buscar opções de contrato:', err));
    }
  }, [open]);

  const TOTAL_STEPS = 4;

  const updateFormData = (fields) =>
    setFormData((prev) => ({ ...prev, ...fields }));

  const handleNext = () => setActiveStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleClose = () => {
    setActiveStep(0);
    setFormData(initialFormData);
    onClose();
  };

  const handleSubmit = async () => {
    const customer = simulation?.customer ?? {};
    const address  = customer?.address ?? {};
    const partners = customer?.relationships ?? [];

    const company        = simulation?.company ?? {};
    const companyAddress = company?.address    ?? {};
    const companyPartners = company?.relationships ?? [];

    // Normalize phone: remove country code "55" if present
    const normalizePhone = (raw) => {
      if (!raw) return '';
      const digits = raw.replace(/\D/g, '');
      const local = digits.startsWith('55') && digits.length === 13 ? digits.slice(2) : digits;
      return formatPhone(local) || local;
    };

    // State can be a string or an object {name}
    const stateStr = (region) => typeof region === 'string' ? region : (region?.name ?? '');

    const payload = {
      simulation_id: simulation?.id,
      client_name: customer.full_name ?? customer.name ?? null,
      simulation_value: simulation?.total_value ?? simulation?.value ?? 0,

      // ── Valores financeiros da simulação ───────────────────────────────────
      sim_financed_value:    simulation?.financed_value    ?? 0,
      sim_entry_value:       simulation?.entry_value       ?? 0,
      sim_installments:      simulation?.installments      ?? '',
      sim_installment_value: simulation?.installment_value ?? 0,
      sim_interest_rate:     simulation?.interest_rate     ?? '',
      sim_iof_rate:          simulation?.iof_rate          ?? '',
      sim_tac_rate:          simulation?.tac_rate          ?? '',
      sim_operational_fee:   simulation?.operational_fee   ?? 0,

      // ── Dados do cliente ───────────────────────────────────────────────────
      sim_customer_email:           customer.email ?? '',
      sim_customer_phone:           normalizePhone(customer.cellphone),
      sim_customer_taxvar:          formatCpfCnpj(customer.taxvar ?? ''),
      sim_customer_business_name:   customer.custom_fields?.business_name ?? '',
      sim_customer_municipal_reg:   customer.custom_fields?.municipal_registration ?? '',
      sim_customer_state_reg:       customer.custom_fields?.state_registration ?? '',
      sim_customer_tax_regime:      customer.custom_fields?.tax_regime ?? '',
      sim_customer_financial_email: customer.custom_fields?.financial_email ?? '',
      sim_customer_financial_phone: normalizePhone(customer.custom_fields?.financial_phone),
      sim_customer_address_place:   address.place ?? '',
      sim_customer_number:          address.number ?? '',
      sim_customer_neighborhood:    address.neighborhood ?? '',
      sim_customer_complement:      address.complement ?? '',
      sim_customer_city:            address.city ?? '',
      sim_customer_state:           stateStr(address.region),
      sim_customer_cep:             address.zipcode ?? '',

      // ── Sócios do cliente ──────────────────────────────────────────────────
      sim_socios: partners.map((p, i) => ({
        id: Date.now() + i,
        nome:     p.people?.full_name ?? '',
        email:    p.people?.email     ?? '',
        telefone: normalizePhone(p.people?.phone),
        cpf:      formatCpfCnpj(p.people?.taxvar ?? ''),
        rg:       p.people?.document ?? '',
      })),

      // ── Dados da revenda (company) ─────────────────────────────────────────
      sim_company_name:           company.name    ?? '',
      sim_revenda_cnpj:           formatCpfCnpj(company.taxvar ?? ''),
      sim_revenda_tax_regime:     company.custom_fields?.tax_regime ?? '',
      sim_revenda_municipal_reg:  company.custom_fields?.municipal_registration ?? '',
      sim_revenda_state_reg:      company.custom_fields?.state_registration ?? '',
      sim_revenda_financial_name: company.custom_fields?.financial_responsible_name ?? '',
      sim_revenda_financial_email: company.custom_fields?.financial_responsible_email ?? '',
      sim_revenda_credit_email:   company.custom_fields?.credit_release_email ?? '',
      // Endereço da revenda
      sim_revenda_address_place:  companyAddress.place        ?? '',
      sim_revenda_number:         companyAddress.number       ?? '',
      sim_revenda_neighborhood:   companyAddress.neighborhood ?? '',
      sim_revenda_complement:     companyAddress.complement   ?? '',
      sim_revenda_city:           companyAddress.city         ?? '',
      sim_revenda_state:          stateStr(companyAddress.region),
      sim_revenda_cep:            companyAddress.zipcode      ?? '',
      // Sócios da revenda
      sim_revenda_socios: companyPartners.map((p, i) => ({
        id: Date.now() + i + 1000,
        nome:  p.people?.full_name ?? '',
        email: p.people?.email     ?? '',
        cpf:   formatCpfCnpj(p.people?.taxvar ?? ''),
        rg:    p.people?.document  ?? '',
      })),

      ...formData,
    };

    try {
      setSubmitting(true);
      await api.post('/v1/contracts', payload);
      handleClose();
    } catch (err) {
      console.error('Erro ao criar contrato:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const stepProps = { simulation, formData, updateFormData, onNext: handleNext, onBack: handleBack, options };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: 3,
          pt: 3,
          pb: 0,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '20px' }}>
            {t('createContractModal.title')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#9e9e9e', mt: 0.5 }}>
            {t('createContractModal.subtitle')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <StepperBar activeStep={activeStep} colors={colors} t={t} />
      <Divider />

      <DialogContent sx={{ px: 3, py: 3, overflowY: 'auto' }}>
        {activeStep === 0 && <Step1ContractData {...stepProps} />}
        {activeStep === 1 && <Step2Creditor {...stepProps} />}
        {activeStep === 2 && <Step3Guarantees {...stepProps} />}
        {activeStep === 3 && (
          <Step4Confirmation
            {...stepProps}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateContractModal;
